import React, { useState } from "react";
import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from "@headlessui/react";
import { ChevronUpDownIcon, CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";

/**
 * Reusable Autocomplete component for UI 2.0
 *
 * @param {Object} props
 * @param {Array} props.options - Array of { value, label } objects
 * @param {string} props.value - Currently selected value
 * @param {function} props.onChange - Callback when value changes (receives value string)
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.label - Optional label text
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string} props.className - Additional classes for the container
 * @param {string} props.dropdownClassName - Additional classes for the dropdown menu (e.g., "tw-min-w-[350px]")
 */
export default function Autocomplete({
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  label,
  disabled = false,
  className = "",
  dropdownClassName = "",
}) {
  const [query, setQuery] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  // Find the selected option object from the value
  const selectedOption = options.find((opt) => opt.value === value) || null;

  // Filter options based on query - only filter when actively typing
  const filteredOptions =
    query === "" || !isTyping
      ? options
      : options.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase())
        );

  const handleChange = (option) => {
    if (option) {
      onChange(option.value);
      setQuery("");
      setIsTyping(false);
    }
  };

  const handleInputChange = (event) => {
    setQuery(event.target.value);
    setIsTyping(true);
  };

  const handleFocus = (event) => {
    // Select all text on focus so user can immediately type to search
    event.target.select();
    setIsTyping(false);
  };

  return (
    <div className={className}>
      {label && (
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
          {label}
        </label>
      )}
      <Combobox value={selectedOption} onChange={handleChange} disabled={disabled}>
        <div className="tw-relative">
          <ComboboxButton className="tw-w-full">
            <ComboboxInput
              className={`tw-w-full tw-rounded-xl tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-700 tw-px-4 tw-py-3 tw-pr-10 tw-text-base dark:tw-text-white tw-placeholder-gray-400 focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500/20 ${
                disabled ? "tw-bg-gray-50 dark:tw-bg-gray-600 tw-cursor-not-allowed" : ""
              }`}
              displayValue={(option) => option?.label || ""}
              onChange={handleInputChange}
              onFocus={handleFocus}
              placeholder={placeholder}
            />
            <span className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3 tw-pointer-events-none">
              <ChevronUpDownIcon
                className="tw-h-5 tw-w-5 tw-text-gray-400"
                aria-hidden="true"
              />
            </span>
          </ComboboxButton>

          <ComboboxOptions className={`tw-absolute tw-z-50 tw-mt-1 tw-max-h-60 tw-w-full tw-overflow-auto tw-rounded-xl tw-bg-white dark:tw-bg-gray-800 tw-py-1 tw-text-base tw-shadow-lg tw-ring-1 tw-ring-black/5 dark:tw-ring-white/10 focus:tw-outline-none ${dropdownClassName}`}>
            {filteredOptions.length === 0 && query !== "" ? (
              <div className="tw-relative tw-cursor-default tw-select-none tw-px-4 tw-py-3 tw-text-gray-500 dark:tw-text-gray-400">
                No results found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <ComboboxOption
                  key={option.value}
                  value={option}
                  className="tw-relative tw-cursor-pointer tw-select-none tw-py-3 tw-pl-10 tw-pr-4 tw-text-gray-900 dark:tw-text-white data-[focus]:tw-bg-blue-50 dark:data-[focus]:tw-bg-blue-900/50"
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`tw-block tw-truncate ${
                          selected ? "tw-font-medium" : "tw-font-normal"
                        }`}
                      >
                        {option.label}
                      </span>
                      {selected && (
                        <span className="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3 tw-text-blue-600 dark:tw-text-blue-400">
                          <CheckIcon className="tw-h-5 tw-w-5" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  );
}
